import {
  OrderDetails,
  LoadingSpinner,
  ModalMessage,
} from "../../components";
import { Container } from "react-bootstrap";
import {
  adminFetchOrderDetailsApi,
  adminSetCancellationStatusApi,
  adminSetDeliveryStatusApi,
  adminSetRefundedStatusApi,
  adminSetReturnedStatusApi,
} from "../../api/ordersApi.js";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getStorageToken, useLogout } from "../../utilities";
import { useSelector } from "react-redux";

export default function AdminOrderDetails() {
  // display order details page for admins
  // admin can edit/delete an order details

  const { id } = useParams();
  const { stayLoggedIn } = useSelector((state) => state.user);
  const logout = useLogout();

  const [orderDetails, setOrderDetails] = useState(null);
  const [delivered, setDelivered] = useState({ status: false, date: false });
  const [paid, setPaid] = useState({ status: false, date: false });
  const [refunded, setRefunded] = useState({ status: false, date: false });
  const [returned, setReturned] = useState({ status: false, date: false });
  const [cancelled, setCancelled] = useState({ status: false});
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchOrderDetails = async (orderId) => {
      // returns array with jsons
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await adminFetchOrderDetailsApi(
          token,
          orderId,
          abortController.signal
        );
        return res;
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderDetails(id)
      .then((res) => {
        setOrderDetails(res);
        setDelivered({ status: res?.isDelivered, date: res?.deliveredAt });
        setPaid({ status: res?.isPaid, date: res?.paidAt });
        setCancelled({status: res?.isCancelled})
        setRefunded({status: res?.refunded, date: res?.refundedAt})
        setReturned({status: res?.returned, date: res?.returnedAt})
      })
      .catch((error) =>
        setErrorMsg("Could not load data. Please try again later.")
      );

    return () => {
      abortController.abort();
    };
  }, [id, stayLoggedIn, logout]);


  const toggleDeliveredStatus = async (newStatus) => {
    // toggles order status, returns json with {status: (Boolean), date: (Date)}
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminSetDeliveryStatusApi(
        token,
        id,
        newStatus,
        abortController.signal
      );
      setDelivered(res);
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg(
        "Could not update the delivery status. Please try again later."
      );
    }
  };

  const toggleCancelledStatus = async (newStatus) => {
    // toggles cancelled status, returns json with {status: (Boolean), date: (Date)}
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminSetCancellationStatusApi(
        token,
        id,
        newStatus,
        abortController.signal
      );
      setCancelled(res);
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg(
        "Could not update the cancellation status. Please try again later."
      );
    }
  };

  const toggleRefundedStatus = async (newStatus) => {
    // toggles refunded status, returns json with {status: (Boolean), date: (Date)}
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminSetRefundedStatusApi(
        token,
        id,
        newStatus,
        abortController.signal
      );
      setRefunded(res);
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg(
        "Could not update the refunded status. Please try again later."
      );
    }
  };

  const toggleReturnedStatus = async (newStatus) => {
    // toggles refunded status, returns json with {status: (Boolean), date: (Date)}
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminSetReturnedStatusApi(
        token,
        id,
        newStatus,
        abortController.signal
      );
      setReturned(res);
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg(
        "Could not update the refunded status. Please try again later."
      );
    }
  };

  return (
    <Container  className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
        <h1>Order Details</h1>
          {isLoading ? <LoadingSpinner /> : null}
          {orderDetails && (
            <OrderDetails
              admin
              order
              data={orderDetails}
              delivered={delivered}
              paid={paid}
              cancelled={cancelled}
              refunded={refunded}
              returned={returned}
              toggleDeliveredStatus={toggleDeliveredStatus}
              toggleCancelledStatus={toggleCancelledStatus}
              toggleReturnedStatus={toggleReturnedStatus}
              toggleRefundedStatus={toggleRefundedStatus}
            />
          )}
    </Container>
  );
}
